import React, { useMemo } from "react";
import {
    IBinding,
    ISelectableValue,
    getValueKey,
    getSelectableValue,
    NoneValueOption,
} from "constants/product-customizations/customization";

import { TCustomizationSelectionSelectableValue } from "constants/product-customizations/customization-selection.types";
import { formatMessage } from "lib/globalize-util";
import { toDataTest } from "helpers/test";
import SelectField from "components/form/select-field";
import MenuItem from "material-ui/MenuItem";
import { themeOptions } from "styles/theme";

type TSelectionProps = {
    attributeId: string;
    label: string;
    target?: IBinding;
};

type TProps<
    T,
    ValueKeyField = {},
    Selected = T & TSelectionProps & TCustomizationSelectionSelectableValue & ValueKeyField,
> = (ValueKeyField & TSelectionProps) & {
    attributeValue: ISelectableValue;
    isRequired?: boolean | undefined;
    readonly?: boolean;
    width?: string;
    inputStyle?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    selected: Selected;
    onChange: (value: Selected) => void;
};

type TSelectableAttributeProps<T, ValueKey> = TProps<T> | TProps<T, { valueKey: ValueKey }>;

export const SelectableAttribute = <T, ValueKey>(props: TSelectableAttributeProps<T, ValueKey>) => {
    const {
        selected,
        attributeValue,
        attributeId,
        label,
        isRequired,
        readonly = false,
        width,
        inputStyle,
        containerStyle,
    } = props;
    const valueKey = "valueKey" in props ? props.valueKey : "";

    const { values: selectableOptions } = attributeValue;

    const handleChange = (e: Event, index: number, value: string) => {
        if (readonly) {
            return e.preventDefault();
        }

        const isNone = !isRequired && index === 0;
        const option = isNone ? NoneValueOption : selectableOptions[isRequired ? index : index - 1];
        const result = <T, Rest>(selected: T, rest: Rest) => ({
            ...selected,
            attributeId,
            label,
            value: value,
            valueId: option?._id ?? "",
            displayValue: option?.name ?? "",
            target: option?.binding,
            ...rest,
        });

        if ("valueKey" in props) {
            props.onChange(result(props.selected, { valueKey: props.valueKey }));
        } else {
            props.onChange(result(props.selected, {}));
        }
    };

    const options = useMemo(() => {
        const items =
            attributeValue.values.map((value) => {
                const key = getValueKey(value);
                return (
                    <MenuItem
                        key={key}
                        value={getSelectableValue(value)}
                        primaryText={value.name ?? key}
                        data-test={`customization-selectable-item-${toDataTest(key)}`}
                    />
                );
            }) || [];

        if (!isRequired) {
            items.unshift(
                <MenuItem
                    key={"none"}
                    value={NoneValueOption.string}
                    primaryText={formatMessage("None")}
                    data-test={"customization-selectable-item-none"}
                />,
            );
        }

        return items;
    }, [attributeValue, isRequired]);

    const dataTest = ["customization", "selectable", attributeId, `${valueKey}`]
        .filter(Boolean)
        .map(toDataTest)
        .join("-");

    return (
        <SelectField
            key={attributeId}
            name={dataTest}
            width={width || "200px"}
            value={selected.value}
            onChange={handleChange}
            data-test={dataTest}
            disabled={readonly}
            disabledStyle={muiStyles.disabledSelect}
            disabledLabelStyle={muiStyles.disabledSelectLabel}
            inputStyle={inputStyle}
            containerStyle={containerStyle}
        >
            {options}
        </SelectField>
    );
};

const muiStyles = {
    disabledSelect: {
        backgroundColor: themeOptions.palette.lighterGrey,
        border: `1px solid ${themeOptions.palette.grey}`,
    },
    disabledSelectLabel: {
        color: "black !important",
    },
} as const;
